import { useCallback } from 'react'
import * as XLSX from 'xlsx'

type ExcelColumn<T> = {
  header: string
  accessor: keyof T | ((item: T) => string | number)
}

type UseExportToExcelOptions<T> = {
  columns: ExcelColumn<T>[]
  fileName?: string
  sheetName?: string
  rtl?: boolean
}

const EXCEL_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const resolveValue = <T,>(
  item: T,
  accessor: ExcelColumn<T>['accessor']
): string | number => {
  if (typeof accessor === 'function') {
    return accessor(item)
  }
  const value = item[accessor]
  if (value === null || value === undefined) return ''
  return value as unknown as string | number
}

const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const useExportToExcel = <T,>({
  columns,
  fileName = 'export',
  sheetName = 'Sheet1',
  rtl = false,
}: UseExportToExcelOptions<T>) => {
  const handleExport = useCallback(
    (data: T[]) => {
      if (!data || data.length === 0) return

      const headers = columns.map((col) => col.header)
      const rows = data.map((item) =>
        columns.map((col) => resolveValue(item, col.accessor))
      )

      const worksheetData = [headers, ...rows]
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

      if (rtl) {
        if (!workbook.Workbook) workbook.Workbook = {}
        if (!workbook.Workbook.Views) workbook.Workbook.Views = []
        if (!workbook.Workbook.Views[0]) workbook.Workbook.Views[0] = {}
        workbook.Workbook.Views[0].RTL = true
      }

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      }) as ArrayBuffer

      const blob = new Blob([excelBuffer], { type: EXCEL_MIME_TYPE })
      downloadBlob(blob, `${fileName}.xlsx`)
    },
    [columns, fileName, sheetName, rtl]
  )

  return { handleExport }
}

export type { ExcelColumn, UseExportToExcelOptions }
export default useExportToExcel
